/**
 * Forms & surveys — seller page (2026-09-07, flag `forms`).
 *
 * Reached from the "Forms" sidebar item (LeftNav, requiresFlag: forms)
 * and from #/forms/<formId> deep links in the form_response
 * notification. Three views on one screen:
 *
 *   list      every form in the workspace with kind, status, response count
 *   edit      the builder — name, kind, description, ordered fields
 *   responses the answers, one column per field, with CSV export
 *
 * Share-by-link mints a per-recipient /form/<token> URL (backend
 * POST /api/forms/:id/share); embedding in a Decision Room happens
 * from the room's Forms section, not here, because the room is where
 * the seller is thinking about that buyer.
 *
 * Permissions are enforced server-side (forms.manage,
 * forms.view_responses); a 403 surfaces as the error banner rather
 * than a hidden control, so a member with a narrowed role learns why.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE, apiRequest, formatWhen } from '../utils/apiRequest';
import FormFill from './FormFill';

const KINDS = [
  { id: 'qualification', label: 'Qualification' },
  { id: 'feedback',      label: 'Feedback' },
  { id: 'intake',        label: 'Onboarding intake' },
  { id: 'win_loss',      label: 'Win / loss' },
  { id: 'custom',        label: 'Custom' },
];
const FIELD_TYPES = [
  { id: 'short_text',    label: 'Short text' },
  { id: 'long_text',     label: 'Long text' },
  { id: 'number',        label: 'Number' },
  { id: 'date',          label: 'Date' },
  { id: 'single_select', label: 'Single choice' },
  { id: 'multi_select',  label: 'Multiple choice' },
  { id: 'dropdown',      label: 'Dropdown' },
  { id: 'rating',        label: 'Rating (1–N)' },
  { id: 'nps',           label: 'NPS (0–10)' },
  { id: 'loss_reason',   label: 'Loss reason (shared list)' },
];
const CHOICE_TYPES = new Set(['single_select', 'multi_select', 'dropdown']);

function kindLabel(id) { return (KINDS.find((k) => k.id === id) || KINDS[4]).label; }

function formIdFromHash() {
  if (typeof window === 'undefined') return null;
  const parts = (window.location.hash || '').replace(/^#\/?/, '').split(/[/?]/);
  return parts[0] === 'forms' && parts[1] ? parts[1] : null;
}

export default function FormsPhase({ authToken, onToast }) {
  const [forms, setForms] = useState(null);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(() => formIdFromHash());
  const [view, setView] = useState(() => (formIdFromHash() ? 'responses' : 'list'));
  const [creating, setCreating] = useState(false);

  const toast = useCallback((m) => { if (typeof onToast === 'function') onToast(m); }, [onToast]);

  const load = useCallback(async () => {
    try {
      const r = await apiRequest('/api/forms', { token: authToken });
      setForms(r.forms || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [authToken]);

  useEffect(() => { load(); }, [load]);

  // Mirror selection into the hash so refresh and the notification
  // deep link land on the same form.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const target = selectedId && view !== 'list' ? `#/forms/${selectedId}` : '#/forms';
    if (window.location.hash !== target) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + target);
    }
  }, [selectedId, view]);

  const selected = useMemo(() => (forms || []).find((f) => f.id === selectedId) || null, [forms, selectedId]);

  const openEdit = (id) => { setSelectedId(id); setView('edit'); setCreating(false); };
  const openResponses = (id) => { setSelectedId(id); setView('responses'); setCreating(false); };
  const backToList = () => { setView('list'); setCreating(false); };

  return (
    <div className="reports-phase admin-phase">
      <div className="phase-header-row">
        <div className="reports-header">
          <h1>Forms</h1>
          <p className="reports-subtitle">
            Qualification, feedback, onboarding intake and win/loss surveys. Embed one in a Decision Room, or send a direct link.
          </p>
        </div>
        {view === 'list' ? (
          <button type="button" className="back-to-dashboard" onClick={() => { setCreating(true); setSelectedId(null); setView('edit'); }}>
            + New form
          </button>
        ) : (
          <button type="button" className="back-to-dashboard" onClick={backToList}>← All forms</button>
        )}
      </div>

      {error && <div role="alert" style={alertStyle}>{error}</div>}

      {view === 'list' && (
        <FormList forms={forms} onEdit={openEdit} onResponses={openResponses} />
      )}

      {view === 'edit' && (creating || selected) && (
        <FormEditor
          key={selected ? selected.id : 'new'}
          authToken={authToken}
          form={creating ? null : selected}
          onSaved={async (saved) => {
            await load();
            setCreating(false);
            setSelectedId(saved.id);
            toast(creating ? `Form "${saved.name}" created.` : 'Form saved.');
          }}
          onArchived={async () => { await load(); backToList(); toast('Form archived.'); }}
          onToast={toast}
        />
      )}

      {view === 'responses' && selectedId && (
        <FormResponses authToken={authToken} formId={selectedId} onEdit={() => openEdit(selectedId)} />
      )}
    </div>
  );
}

// ─── List ──────────────────────────────────────────────────────────

function FormList({ forms, onEdit, onResponses }) {
  if (!forms) return <div style={{ color: 'var(--room-text-muted, #64748b)' }}>Loading…</div>;
  if (forms.length === 0) {
    return (
      <div style={cardStyle}>
        <p style={{ margin: 0, color: 'var(--room-text-muted, #64748b)' }}>
          No forms yet. Create one, or open a Decision Room's Forms section — three starter forms are added the first time you look.
        </p>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {forms.map((f) => (
        <div key={f.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--room-text-strong, #0f172a)' }}>{f.name}</div>
            <div style={{ fontSize: 13, color: 'var(--room-text-muted, #64748b)', marginTop: 2 }}>
              {kindLabel(f.kind)} · {f.responseCount ?? 0} response{(f.responseCount ?? 0) === 1 ? '' : 's'} · updated {formatWhen(f.updatedAt)}
            </div>
          </div>
          <StatusChip status={f.status} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => onResponses(f.id)} style={ghostBtn}>Responses</button>
            <button type="button" onClick={() => onEdit(f.id)} style={ghostBtn}>Edit</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusChip({ status }) {
  // Semantic pastel pairs stay hardcoded (theming rules: never split a pair).
  const map = {
    active:   { bg: '#ecfdf5', ink: '#065f46', label: 'Active' },
    draft:    { bg: '#f1f5f9', ink: '#475569', label: 'Draft' },
    archived: { bg: '#fef3c7', ink: '#92400e', label: 'Archived' },
  };
  const s = map[status] || map.draft;
  return <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999, background: s.bg, color: s.ink }}>{s.label}</span>;
}

// ─── Editor ────────────────────────────────────────────────────────

function newField() {
  return { key: Math.random().toString(36).slice(2), label: '', fieldType: 'short_text', required: false, optionsText: '', scale: 5 };
}

function toDraftFields(fields) {
  return (fields || []).map((f) => ({
    key: f.id || Math.random().toString(36).slice(2),
    label: f.label || '',
    fieldType: f.fieldType || 'short_text',
    required: !!f.required,
    optionsText: (f.options || []).join('\n'),
    scale: (f.config && f.config.scale) || 5,
  }));
}

function toApiFields(draft) {
  return draft.map((f) => ({
    label: f.label,
    fieldType: f.fieldType,
    required: !!f.required,
    options: CHOICE_TYPES.has(f.fieldType) ? f.optionsText.split('\n').map((s) => s.trim()).filter(Boolean) : [],
    config: f.fieldType === 'rating' ? { scale: Number(f.scale) || 5 } : {},
  }));
}

function FormEditor({ authToken, form, onSaved, onArchived, onToast }) {
  const [name, setName] = useState(form ? form.name : '');
  const [kind, setKind] = useState(form ? form.kind : 'custom');
  const [description, setDescription] = useState(form ? (form.description || '') : '');
  const [status, setStatus] = useState(form ? form.status : 'draft');
  const [fields, setFields] = useState(() => (form && form.fields ? toDraftFields(form.fields) : []));
  const [loadingFields, setLoadingFields] = useState(!!(form && !form.fields));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(false);
  const [previewValues, setPreviewValues] = useState({});
  const [share, setShare] = useState(null); // { url }
  const [shareEmail, setShareEmail] = useState('');
  const [shareName, setShareName] = useState('');
  const [sharing, setSharing] = useState(false);

  // The list endpoint omits fields; fetch the full definition once.
  useEffect(() => {
    if (!form || form.fields) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await apiRequest(`/api/forms/${form.id}`, { token: authToken });
        if (!cancelled) setFields(toDraftFields(r.form.fields));
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoadingFields(false);
      }
    })();
    return () => { cancelled = true; };
  }, [form, authToken]);

  const updateField = (key, patch) => setFields((prev) => prev.map((f) => (f.key === key ? { ...f, ...patch } : f)));
  const move = (idx, dir) => setFields((prev) => {
    const next = [...prev];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return prev;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });

  const save = async (e) => {
    if (e) e.preventDefault();
    if (!name.trim()) { setError('Give the form a name.'); return; }
    if (fields.length === 0) { setError('Add at least one question.'); return; }
    setSaving(true);
    setError(null);
    try {
      const body = { name: name.trim(), kind, description: description.trim() || null, status, fields: toApiFields(fields) };
      const r = form
        ? await apiRequest(`/api/forms/${form.id}`, { token: authToken, method: 'PATCH', body })
        : await apiRequest('/api/forms', { token: authToken, method: 'POST', body });
      onSaved(r.form);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    if (!form) return;
    if (!window.confirm(`Archive "${form.name}"? Responses are kept; the form stops accepting new ones.`)) return;
    try {
      await apiRequest(`/api/forms/${form.id}`, { token: authToken, method: 'DELETE' });
      onArchived();
    } catch (err) {
      setError(err.message);
    }
  };

  const mintLink = async (e) => {
    e.preventDefault();
    if (!form) return;
    setSharing(true);
    setError(null);
    try {
      const r = await apiRequest(`/api/forms/${form.id}/share`, { token: authToken, method: 'POST', body: { email: shareEmail.trim(), name: shareName.trim() || undefined } });
      setShare({ url: r.url, reused: r.reused, email: shareEmail.trim() });
    } catch (err) {
      setError(err.message);
    } finally {
      setSharing(false);
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); onToast('Link copied.'); }
    catch (_) { onToast('Could not copy — select the link manually.'); }
  };

  const previewFields = toApiFields(fields).map((f, i) => ({ ...f, id: fields[i].key, options: f.fieldType === 'loss_reason' ? [{ id: 'x', label: '(company loss reasons)' }] : f.options }));

  return (
    <form onSubmit={save} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 18 }}>
      {error && <div role="alert" style={alertStyle}>{error}</div>}

      <div style={cardStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
          <label style={labelStyle}>Name
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} style={inputStyle} placeholder="Post-demo feedback" />
          </label>
          <label style={labelStyle}>Kind
            <select value={kind} onChange={(e) => setKind(e.target.value)} style={inputStyle}>
              {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          </label>
          <label style={labelStyle}>Status
            <select value={status} onChange={(e) => setStatus(e.target.value)} style={inputStyle}>
              <option value="draft">Draft (not shareable)</option>
              <option value="active">Active</option>
            </select>
          </label>
        </div>
        <label style={{ ...labelStyle, marginTop: 14 }}>Intro text <span style={hint}>(optional — shown above the questions)</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={1000} style={{ ...inputStyle, resize: 'vertical' }} />
        </label>
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Questions</h3>
          <button type="button" onClick={() => setPreview((v) => !v)} style={ghostBtn}>{preview ? 'Back to editing' : 'Preview'}</button>
        </div>
        {loadingFields ? (
          <div style={{ color: 'var(--room-text-muted, #64748b)' }}>Loading…</div>
        ) : preview ? (
          <div style={{ padding: 4 }}>
            <FormFill fields={previewFields} values={previewValues} onChange={(id, v) => setPreviewValues((p) => ({ ...p, [id]: v }))} tone="room" />
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {fields.map((f, idx) => (
                <div key={f.key} style={{ border: '1px solid var(--room-border-soft, #f1f5f9)', borderRadius: 12, padding: 12, background: 'var(--room-surface, #f8fafc)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(160px, 1fr) auto', gap: 10, alignItems: 'end' }}>
                    <label style={labelStyle}>Question {idx + 1}
                      <input type="text" value={f.label} maxLength={200} onChange={(e) => updateField(f.key, { label: e.target.value })} style={inputStyle} placeholder="What's the one thing we'd need to get right?" />
                    </label>
                    <label style={labelStyle}>Type
                      <select value={f.fieldType} onChange={(e) => updateField(f.key, { fieldType: e.target.value })} style={inputStyle}>
                        {FIELD_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                      </select>
                    </label>
                    <div style={{ display: 'flex', gap: 4, paddingBottom: 2 }}>
                      <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} style={iconBtn} title="Move up">↑</button>
                      <button type="button" onClick={() => move(idx, 1)} disabled={idx === fields.length - 1} style={iconBtn} title="Move down">↓</button>
                      <button type="button" onClick={() => setFields((prev) => prev.filter((x) => x.key !== f.key))} style={{ ...iconBtn, color: '#dc2626' }} title="Remove">×</button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--room-text-body, #334155)' }}>
                      <input type="checkbox" checked={f.required} onChange={(e) => updateField(f.key, { required: e.target.checked })} /> Required
                    </label>
                    {f.fieldType === 'rating' && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--room-text-body, #334155)' }}>
                        Scale 1 to
                        <input type="number" min={2} max={10} value={f.scale} onChange={(e) => updateField(f.key, { scale: e.target.value })} style={{ ...inputStyle, width: 70 }} />
                      </label>
                    )}
                    {f.fieldType === 'loss_reason' && (
                      <span style={hint}>Choices come from your workspace's shared loss reasons.</span>
                    )}
                  </div>
                  {CHOICE_TYPES.has(f.fieldType) && (
                    <label style={{ ...labelStyle, marginTop: 10 }}>Choices <span style={hint}>(one per line, at least two)</span>
                      <textarea value={f.optionsText} onChange={(e) => updateField(f.key, { optionsText: e.target.value })} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                    </label>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setFields((prev) => [...prev, newField()])} style={{ ...ghostBtn, marginTop: 12 }} disabled={fields.length >= 40}>+ Add question</button>
          </>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="submit" disabled={saving} style={primaryBtn}>{saving ? 'Saving…' : (form ? 'Save changes' : 'Create form')}</button>
        {form && form.status !== 'archived' && <button type="button" onClick={archive} style={{ ...ghostBtn, color: '#dc2626' }}>Archive</button>}
      </div>

      {form && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>Send a direct link</h3>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>
            Each link is tied to one recipient, so you know who answered. {form.status !== 'active' && <strong>Activate the form first.</strong>}
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end' }}>
            <label style={{ ...labelStyle, flex: '1 1 220px' }}>Recipient email
              <input type="email" value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} style={inputStyle} placeholder="cfo@prospect.com" />
            </label>
            <label style={{ ...labelStyle, flex: '1 1 160px' }}>Name <span style={hint}>(optional)</span>
              <input type="text" value={shareName} onChange={(e) => setShareName(e.target.value)} style={inputStyle} />
            </label>
            <button type="button" onClick={mintLink} disabled={sharing || form.status !== 'active' || !shareEmail.includes('@')} style={ghostBtn}>{sharing ? 'Creating…' : 'Create link'}</button>
          </div>
          {share && (
            <div style={{ marginTop: 12, padding: 12, borderRadius: 10, background: 'var(--room-surface, #f8fafc)', border: '1px solid var(--room-border-soft, #f1f5f9)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{ fontSize: 13, wordBreak: 'break-all', color: 'var(--room-text-strong, #0f172a)' }}>{share.url}</code>
              <button type="button" onClick={() => copy(share.url)} style={ghostBtn}>Copy</button>
              <span style={hint}>{share.reused ? `Existing link for ${share.email}` : `Link for ${share.email}`}</span>
            </div>
          )}
        </div>
      )}
    </form>
  );
}

// ─── Responses ─────────────────────────────────────────────────────

function FormResponses({ authToken, formId, onEdit }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiRequest(`/api/forms/${formId}/responses`, { token: authToken });
        if (!cancelled) setData(r);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [authToken, formId]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await fetch(`${API_BASE}/api/forms/${formId}/responses.csv`, { headers: { Authorization: `Bearer ${authToken}` } });
      if (!r.ok) throw new Error(`Export failed (${r.status})`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(data && data.form && data.form.name) || 'form'}-responses.csv`.replace(/[^a-z0-9.-]+/gi, '-');
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  if (error) return <div role="alert" style={alertStyle}>{error}</div>;
  if (!data) return <div style={{ color: 'var(--room-text-muted, #64748b)' }}>Loading…</div>;

  const fields = data.form.fields || [];
  const responses = data.responses || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>{data.form.name}</h2>
          <div style={{ fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>{kindLabel(data.form.kind)} · {responses.length} response{responses.length === 1 ? '' : 's'}</div>
        </div>
        <button type="button" onClick={onEdit} style={ghostBtn}>Edit form</button>
        <button type="button" onClick={exportCsv} disabled={exporting || responses.length === 0} style={ghostBtn}>{exporting ? 'Exporting…' : 'Export CSV'}</button>
      </div>

      {responses.length === 0 ? (
        <div style={cardStyle}><p style={{ margin: 0, color: 'var(--room-text-muted, #64748b)' }}>No responses yet.</p></div>
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 640, fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--room-surface, #f8fafc)' }}>
                <th style={th}>Who</th>
                <th style={th}>When</th>
                {fields.map((f) => <th key={f.id} style={th}>{f.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {responses.map((r) => {
                const byField = new Map((r.answers || []).map((a) => [a.fieldId, a]));
                return (
                  <tr key={r.id} style={{ borderTop: '1px solid var(--room-border-soft, #f1f5f9)' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: 'var(--room-text-strong, #0f172a)' }}>{r.respondentName || '—'}</div>
                      <div style={{ color: 'var(--room-text-muted, #64748b)' }}>{r.respondentEmail || ''}</div>
                    </td>
                    <td style={td}>{formatWhen(r.submittedAt)}</td>
                    {fields.map((f) => {
                      const a = byField.get(f.id);
                      let text = '';
                      if (a) {
                        if (f.fieldType === 'loss_reason') text = r.lossReasonLabel || String(a.value);
                        else if (Array.isArray(a.value)) text = a.value.join(', ');
                        else text = String(a.value);
                      }
                      return <td key={f.id} style={td}>{text}</td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Styles (tokens, never hardcoded neutrals) ─────────────────────

const cardStyle = { background: 'var(--room-card, #fff)', border: '1px solid var(--room-border, #e2e8f0)', borderRadius: 14, padding: 18, color: 'var(--room-text-strong, #0f172a)' };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--room-text-body, #334155)' };
const hint = { fontWeight: 400, color: 'var(--room-text-subtle, #94a3b8)' };
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 10, border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)', color: 'var(--room-text-strong, #0f172a)', fontSize: 14, fontFamily: 'inherit', fontWeight: 400 };
const ghostBtn = { padding: '8px 14px', borderRadius: 10, border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)', color: 'var(--room-text-body, #334155)', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const primaryBtn = { padding: '10px 18px', borderRadius: 10, border: '1px solid #7c3aed', background: '#7c3aed', color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const iconBtn = { width: 32, height: 32, borderRadius: 8, border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)', color: 'var(--room-text-body, #334155)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14 };
const alertStyle = { padding: '10px 12px', borderRadius: 10, background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#dc2626', fontSize: 14 };
const th = { textAlign: 'left', padding: '10px 12px', fontWeight: 600, color: 'var(--room-text-muted, #64748b)', whiteSpace: 'nowrap' };
const td = { padding: '10px 12px', verticalAlign: 'top', color: 'var(--room-text-body, #334155)' };
